import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {useToggle} from '../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import grinningEmoji from '../../../../assets/images/grinningEmoji.svg';
import {SkinTonesEnum} from '../../../../models/api/preferencesModel';
import {EmojiDefinition} from '../../../../services/emojis/emojis';
import {Emoji} from '../../emoji/emoji';
import {CustomEmojiDialog} from './emojiDropdownDialog';

/*
 * Props.
 */

interface EmojiDropdownFooterProps {
  emoji?: EmojiDefinition;
  skinTone: SkinTonesEnum;
}

/*
 * Style.
 */

const StyledFooterDiv = styled.div`
  display: grid;
  grid-template-areas: 'emoji . details . add-emoji';
  grid-template-columns: auto 15px 1fr 15px auto;
  align-items: center;

  width: 100%;
  height: 60px;
  padding: 0 15px;

  background: ${(p) => p.theme.foreground};
  border-top: 1px solid ${(p) => p.theme.greys.shade30};
`;

const StyledEmojiDiv = styled.div`
  grid-area: emoji;
`;

const StyledDetailsDiv = styled.div`
  grid-area: details;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const StyledNameDiv = styled.div`
  margin-bottom: 1px;

  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.semibold};
  text-transform: capitalize;
  ${ellipsis()};
`;

const StyledNameDivDisabled = styled(StyledNameDiv)`
  color: ${(p) => p.theme.greys.shade60};
`;

const StyledShortcodeDiv = styled.div`
  margin-bottom: 1px;

  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.medium};
  color: ${(p) => p.theme.greys.shade50};
  ${ellipsis()};
`;

const StyledAddEmojiButton = styled(Button)`
  grid-area: add-emoji;
`;

/*
 * Component.
 */

export const EmojiDropdownFooter: FC<EmojiDropdownFooterProps> = ({emoji, skinTone}) => {
  const [isDialogOpen, toggleDialog] = useToggle();
  return (
    <StyledFooterDiv>
      {emoji ? renderEmoji(emoji, skinTone) : renderPlaceholder(toggleDialog)}
      {isDialogOpen && <CustomEmojiDialog onRequestClose={toggleDialog} />}
    </StyledFooterDiv>
  );
};

function renderPlaceholder(onClickAddEmoji: Handler) {
  return (
    <>
      <StyledEmojiDiv>
        <img alt="" src={grinningEmoji} />
      </StyledEmojiDiv>
      <StyledDetailsDiv>
        <StyledNameDivDisabled>Grinning</StyledNameDivDisabled>
        <StyledShortcodeDiv>:grinning:</StyledShortcodeDiv>
      </StyledDetailsDiv>
      <StyledAddEmojiButton onClick={onClickAddEmoji}>
        <Trans id="emojidropdownfooter-addemoji">Add Emoji</Trans>
      </StyledAddEmojiButton>
    </>
  );
}

function renderEmoji(emoji: EmojiDefinition, skinTone: SkinTonesEnum) {
  return (
    <>
      <StyledEmojiDiv>
        <Emoji useSheet emoji={emoji} size={32} skinTone={skinTone} />
      </StyledEmojiDiv>
      <StyledDetailsDiv>
        <StyledNameDiv>{emoji.shortcode.split(/[-_]/).join(' ')}</StyledNameDiv>
        <StyledShortcodeDiv>:{emoji.shortcode}:</StyledShortcodeDiv>
      </StyledDetailsDiv>
    </>
  );
}
